import { useCallback, useEffect, useState } from 'react';
import { getProfile, updateProfile } from '../services/profile';
import { buildProfileFormData, getProfilePhotoUrl, mapProfileToForm } from '../utils/profileMappers';

export function useProfile({ enabled = true, classId } = {}) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [saveMessage, setSaveMessage] = useState(null);

  const queryParams = classId ? { class_id: classId } : undefined;

  const fetchProfile = useCallback(async () => {
    if (!enabled) return;

    setLoading(true);
    setError(null);
    try {
      const res = await getProfile(queryParams);
      setUser(res?.data?.user || null);
    } catch (e) {
      setError(e?.message || 'Failed to load profile');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [enabled, classId]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const saveProfile = useCallback(
    async (form, profileImageFile) => {
      setSaving(true);
      setSaveError(null);
      setSaveMessage(null);

      try {
        const formData = buildProfileFormData(form, profileImageFile);
        const res = await updateProfile(formData, queryParams);
        const updatedUser = res?.data?.user || null;
        setUser(updatedUser);
        setSaveMessage(res?.message || 'Profile updated successfully');
        return updatedUser;
      } catch (e) {
        setSaveError(e?.message || 'Failed to update profile');
        throw e;
      } finally {
        setSaving(false);
      }
    },
    [classId]
  );

  return {
    user,
    loading,
    saving,
    error,
    saveError,
    saveMessage,
    profileForm: user ? mapProfileToForm(user) : null,
    profilePhotoUrl: getProfilePhotoUrl(user),
    refetch: fetchProfile,
    saveProfile,
    clearSaveFeedback: () => {
      setSaveError(null);
      setSaveMessage(null);
    },
  };
}
